import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Activity, BarChart3, Globe, Clock, ArrowRight, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import api from '../lib/api'
import { faviconUrl } from '../lib/favicon'

// Lokalizovaný název nástroje. Klíče i18n: tools.{id_s_podtrzitkem}.name; fallback český název.
const TOOL_LABEL: Record<string, string> = {
  robots: 'Robots.txt', sitemap: 'Sitemap', schema: 'Strukturovaná data', serp: 'Náhled SERP',
  hreflang: 'Hreflang', mobile: 'Mobilní optimalizace', security: 'Bezpečnostní hlavičky',
  pagespeed: 'Rychlost (PageSpeed)', lighthouse: 'Lighthouse', crux: 'CrUX data',
  'safe-browsing': 'Safe Browsing', indexation: 'Indexace', 'llms-txt': 'llms.txt',
  redirects: 'Přesměrování', content: 'Analýza obsahu', technology: 'Detekce technologií',
  accessibility: 'Přístupnost', wcag: 'WCAG audit', ux: 'UX audit', cookies: 'Cookies / GDPR',
  dns: 'DNS / SSL', 'broken-links': 'Mrtvé odkazy', compare: 'Srovnání webů', bulk: 'Hromadná kontrola',
  geo: 'GEO / AI', 'ai-citation': 'AI citace', 'geo-rewrite': 'GEO přepis', 'ai-answer': 'AI odpověď',
}

export function useToolLabel() {
  const { t } = useTranslation()
  return (id: string) => t(`tools.${id.replace(/-/g, '_')}.name`, { defaultValue: TOOL_LABEL[id] || id })
}

function timeAgo(iso: string, lang: string): string {
  const d = new Date(iso.replace(' ', 'T'))
  const diff = (Date.now() - d.getTime()) / 1000
  if (isNaN(diff)) return ''
  const rtf = new Intl.RelativeTimeFormat(lang || 'cs', { numeric: 'auto' })
  if (diff < 60) return rtf.format(-Math.floor(diff), 'second')
  if (diff < 3600) return rtf.format(-Math.floor(diff / 60), 'minute')
  if (diff < 86400) return rtf.format(-Math.floor(diff / 3600), 'hour')
  if (diff < 2592000) return rtf.format(-Math.floor(diff / 86400), 'day')
  return d.toLocaleDateString(lang || 'cs')
}

interface Summary { total: number; today: number; week?: number; top_tools: { tool: string; count: number }[] }
interface RecentRow { tool: string; domain: string; url?: string; score?: number | null; created_at: string }
interface WebsiteRow { domain: string; total: number; last_checked: string; tools: { tool: string; count: number }[]; title?: string | null; description?: string | null; score?: number | null; has_detail?: boolean }

// Náhled (screenshot) webu přes náš veřejný endpoint — stejný jako u analýz.
const shot = (domain: string) => `${api.defaults.baseURL}/analyses/screenshot?url=${encodeURIComponent('https://' + domain)}&w=260&h=164`

function scoreColor(s: number) {
  if (s >= 80) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
  if (s >= 50) return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
  return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
}

/** Jeden web ve výpisu — screenshot + titulek + popis + chipy nástrojů. Na mobilu stack, na sm+ vedle. */
function WebsiteCard({ w }: { w: WebsiteRow }) {
  const { t, i18n } = useTranslation()
  const toolLabel = useToolLabel()
  const [expanded, setExpanded] = useState(false)
  const CAP = 6
  const tools = expanded ? w.tools : w.tools.slice(0, CAP)
  const hidden = w.tools.length - tools.length

  const detailPath = `/web/${w.domain}`
  const hasScore = typeof w.score === 'number'

  return (
    <article className="flex flex-col sm:flex-row gap-3 sm:gap-4 rounded-xl border border-gray-100 dark:border-white/10 bg-gray-50/60 dark:bg-white/5 p-3 sm:p-3.5">
      {/* Screenshot → detail webu s použitými nástroji (interní prolink). Mobil: celá šířka nahoře. */}
      <Link to={detailPath} className="relative w-full sm:w-[150px] aspect-[260/164] shrink-0 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 bg-white dark:bg-white/5 group">
        <img src={shot(w.domain)} alt={`${w.title || w.domain} – náhled`} loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.04]"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
        {hasScore && <span className={`absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-xs font-bold shadow-sm ${scoreColor(w.score as number)}`}>{w.score}</span>}
        <Globe className="absolute inset-0 m-auto w-6 h-6 text-gray-200 dark:text-white/20 -z-10" />
      </Link>

      <div className="min-w-0 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex items-center gap-2">
            <img src={faviconUrl(w.domain)} alt="" loading="lazy" width={16} height={16}
              className="w-4 h-4 rounded shrink-0 bg-white" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }} />
            <a href={`https://${w.domain}`} target="_blank" rel="noopener"
              className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate hover:text-primary-600 dark:hover:text-primary-400 transition">
              {w.title || w.domain}
            </a>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {hasScore && <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${scoreColor(w.score as number)}`}>{w.score}/100</span>}
            <span className="text-xs text-gray-400">{timeAgo(w.last_checked, i18n.language)}</span>
          </div>
        </div>
        {w.title && <p className="text-xs text-emerald-600 dark:text-emerald-400 truncate mt-0.5">{w.domain}</p>}
        {w.description && <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{w.description}</p>}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tools.map((tl) => (
            <span key={tl.tool} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-white/10 border border-gray-200/70 dark:border-white/10 text-xs font-medium text-gray-700 dark:text-gray-200">
              {toolLabel(tl.tool)}{tl.count > 1 && <span className="text-gray-400">×{tl.count}</span>}
            </span>
          ))}
          {(hidden > 0 || expanded) && (
            <button type="button" onClick={() => setExpanded(!expanded)}
              className="px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-300 text-xs font-semibold hover:bg-primary-100 dark:hover:bg-primary-500/20 transition">
              {expanded ? t('toolusage.show_less', { defaultValue: 'Méně' }) : `+${hidden}`}
            </button>
          )}
        </div>
        <Link to={detailPath} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 mt-2 self-start">
          {t('toolusage.view_detail', { defaultValue: 'Zobrazit detail webu' })}<ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </article>
  )
}

/** Výpis kontrolovaných webů, u každého chipy použitých nástrojů. */
function WebsiteUsageList({ websites }: { websites: WebsiteRow[] }) {
  const { t } = useTranslation()
  if (!websites.length) {
    return <p className="text-sm text-gray-400">{t('toolusage.empty', { defaultValue: 'Zatím žádné kontroly.' })}</p>
  }
  return (
    <div className="space-y-3">
      {websites.map((w) => <WebsiteCard key={w.domain} w={w} />)}
    </div>
  )
}

/** Prezentační statistika používání nástrojů (souhrn + top nástroje + výpis webů / posledních kontrol). */
export default function ToolUsageStats({ summary, recent, websites, showUrl = false }: {
  summary: Summary; recent?: RecentRow[]; websites?: WebsiteRow[]; showUrl?: boolean
}) {
  const { t, i18n } = useTranslation()
  const toolLabel = useToolLabel()
  const top = summary?.top_tools ?? []
  const maxCount = top.length ? Math.max(...top.map((x) => x.count)) : 1

  const tiles = [
    { icon: Activity, label: t('toolusage.total', { defaultValue: 'Celkem kontrol' }), value: summary?.total ?? 0 },
    { icon: Clock, label: t('toolusage.today', { defaultValue: 'Dnes' }), value: summary?.today ?? 0 },
    ...(summary?.week !== undefined ? [{ icon: BarChart3, label: t('toolusage.week', { defaultValue: 'Posledních 7 dní' }), value: summary.week }] : []),
  ]

  return (
    <div className="space-y-6">
      {/* Souhrnné dlaždice */}
      <div className={`grid gap-3 ${tiles.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {tiles.map((tile, i) => (
          <div key={i} className="rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-3 sm:p-4">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1">
              <tile.icon className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px] sm:text-xs font-medium leading-tight">{tile.label}</span>
            </div>
            <div className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">{(tile.value ?? 0).toLocaleString(i18n.language || 'cs')}</div>
          </div>
        ))}
      </div>

      {/* Top nástroje */}
      {top.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3">{t('toolusage.top_tools', { defaultValue: 'Nejpoužívanější nástroje' })}</h3>
          <div className="space-y-2">
            {top.map((row) => (
              <div key={row.tool} className="flex items-center gap-2 sm:gap-3">
                <span className="w-24 sm:w-40 shrink-0 text-xs sm:text-sm text-gray-700 dark:text-gray-300 truncate">{toolLabel(row.tool)}</span>
                <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500" style={{ width: `${Math.max(4, (row.count / maxCount) * 100)}%` }} />
                </div>
                <span className="w-9 sm:w-12 shrink-0 text-right text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-300">{row.count.toLocaleString(i18n.language || 'cs')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kontrolované weby (seskupené) nebo chronologický výpis (admin) */}
      <div>
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3">
          {websites ? t('toolusage.websites', { defaultValue: 'Kontrolované weby' }) : t('toolusage.recent', { defaultValue: 'Poslední kontroly' })}
        </h3>
        {websites ? (
          <WebsiteUsageList websites={websites} />
        ) : !recent || recent.length === 0 ? (
          <p className="text-sm text-gray-400">{t('toolusage.empty', { defaultValue: 'Zatím žádné kontroly.' })}</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-white/5">
            {recent.map((row, i) => (
              <li key={i} className="flex items-center gap-3 py-2.5">
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-300 text-xs font-semibold">{toolLabel(row.tool)}</span>
                <span className="flex items-center gap-1.5 min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-300">
                  <Globe className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <span className="truncate">{showUrl && row.url ? row.url : row.domain}</span>
                </span>
                {typeof row.score === 'number' && (
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-bold ${scoreColor(row.score)}`}>{row.score}</span>
                )}
                <span className="shrink-0 text-xs text-gray-400 w-24 text-right">{timeAgo(row.created_at, i18n.language)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

const PUBLIC_PAGE = 12

/** Veřejná karta (sociální důkaz) — používání nástrojů všemi návštěvníky. Hledání + řazení + stránkování. */
export function PublicToolUsageCard({ className = '' }: { className?: string }) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<'recent' | 'score' | 'checks'>('recent')
  const [page, setPage] = useState(0)
  const { data } = useQuery({
    queryKey: ['tool-usage-public', q, sort, page],
    queryFn: () => api.get(`/tools/usage?limit=${PUBLIC_PAGE}&offset=${page * PUBLIC_PAGE}&sort=${sort}&q=${encodeURIComponent(q)}`).then((r) => r.data),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
  // Skryj kartu jen když vůbec nic není (a uživatel nehledá).
  if (!data || ((data.summary?.total ?? 0) === 0 && q === '')) return null
  const total = data.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PUBLIC_PAGE))
  const SORTS = [
    { id: 'recent' as const, label: t('toolusage.sort_recent', { defaultValue: 'Nejnovější' }) },
    { id: 'score' as const, label: t('toolusage.sort_score', { defaultValue: 'Skóre' }) },
    { id: 'checks' as const, label: t('toolusage.sort_checks', { defaultValue: 'Počet kontrol' }) },
  ]
  return (
    <section className={`bg-white dark:bg-white/5 rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-5 h-5 text-primary-500" />
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('toolusage.public_title', { defaultValue: 'Co lidé právě kontrolují' })}</h2>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">{t('toolusage.public_desc', { defaultValue: 'Živý přehled používání našich SEO nástrojů.' })}</p>

      {/* Hledání + řazení */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0) }}
            placeholder={t('toolusage.search_ph', { defaultValue: 'Hledat doménu…' })}
            aria-label={t('toolusage.search_ph', { defaultValue: 'Hledat doménu…' })}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-0.5">
          {SORTS.map((s) => (
            <button key={s.id} onClick={() => { setSort(s.id); setPage(0) }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${sort === s.id ? 'bg-white dark:bg-white/10 text-primary-700 dark:text-primary-300 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <ToolUsageStats summary={data.summary} websites={data.websites || []} />

      {/* Stránkování */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-5">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
            aria-label={t('common.prev', { defaultValue: 'Předchozí' })}
            className="p-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 transition">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">{page + 1} / {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
            aria-label={t('common.next', { defaultValue: 'Další' })}
            className="p-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 transition">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  )
}

/** Karta vlastního používání pro přihlášeného uživatele (na dashboardu). */
export function MyToolUsageCard({ className = '' }: { className?: string }) {
  const { t } = useTranslation()
  const { data } = useQuery({
    queryKey: ['tool-usage-mine'],
    queryFn: () => api.get('/me/tool-usage').then((r) => r.data),
    staleTime: 30_000,
  })
  return (
    <section className={`bg-white dark:bg-white/5 rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary-500" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('toolusage.mine_title', { defaultValue: 'Vaše používání nástrojů' })}</h2>
        </div>
        <Link to="/tools" className="text-sm font-medium text-primary-600 hover:text-primary-500">{t('toolusage.go_tools', { defaultValue: 'Nástroje' })}</Link>
      </div>
      <ToolUsageStats summary={data?.summary ?? { total: 0, today: 0, week: 0, top_tools: [] }} websites={data?.websites || []} />
    </section>
  )
}
